import React, { createContext, useContext, useState } from "react";
import { useUserContext } from "../../context/UserProvider";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getNorthRunHistory } from "./onboardingFlags";

const onboardingContext = createContext(null);

function useOnboardingContext() {
  return useContext(onboardingContext);
}

const Onboarding = ({ onboardingID, pollData, selectedRace, firstName, setHasCompletedOnboardingBefore }) => {
  let { userData, user, userDetails, membership, membershipAccess } = useUserContext();
  let [currentStep, setCurrentStep] = useState(1);
  let [answers, setAnswers] = useState(
    pollData.questions.map((question) => {
      if (question.type === "chips") return { question: question.question, answer: [] };
      if (question.type === "text") return { question: question.question, answer: "" };
      return question;
    })
  );
  let steps = pollData?.questions.length;
  let colorGradient = `linear-gradient(90deg,${pollData.color}, color-mix(in hsl, ${pollData.color}, white 30%))`;
  let raceName = selectedRace?.raceInfo?.name;
  let currentQuestion = pollData?.questions[currentStep - 1];
  let selectedCount = answers[currentStep - 1]?.answer?.length;
  let progressPercent = Math.round((currentStep / (steps + 1)) * 100);

  let continueEnabled = false;
  let textareaMinAnswer = 1;
  if (currentQuestion.type === "chips") {
    if (selectedCount > currentQuestion.max || selectedCount < currentQuestion.min) {
      continueEnabled = false;
    } else {
      continueEnabled = true;
    }
  }
  if (currentQuestion.type === "text") {
    if (answers[currentStep - 1].answer.length < textareaMinAnswer) {
      continueEnabled = false;
    } else {
      continueEnabled = true;
    }
  }
  
  function nextQuestion() {
    if (!continueEnabled) return;
    if (currentStep >= steps) {
      let formatAnswers = answers.map((a) => {
        return { question: a.question, answer: typeof a.answer === "string" ? a.answer : a.answer.toString() };
      });

      let writeData = {
        raceName: selectedRace?.raceInfo?.name,
        legName: selectedRace?.raceInfo?.legName,
        name: userDetails.firstName + " " + userDetails.lastName,
        email: userData.email,
        subscribedRaceCount: Object.keys(userData.subscribedRaces).length,
        answers: formatAnswers,
        createdAt: new Date().toString(),
        answeredAt: serverTimestamp(),
        onboardingID: onboardingID,
        momentumMember: membership === true,
        membershipState: membershipAccess?.state || "none",
      };
      let northRun = getNorthRunHistory(userData.subscribedRaces, selectedRace);
      if (northRun) {
        writeData.northRunStatus = northRun.status;
        writeData.previousNorthRunRaces = northRun.previousRaces;
      }

      //Write answer
      setDoc(doc(db, "races", selectedRace.legId, "onboarding", user.uid), writeData);
      setHasCompletedOnboardingBefore(true);
    } else {
      setCurrentStep(currentStep + 1);
    }
  }
  return (
    <onboardingContext.Provider value={{ currentStep, currentQuestion, colorGradient, answers, setAnswers }}>
      <div className="flex justify-around flex-col h-full">
        <div id="header" className="text-center">
          <p style={{ color: pollData?.color }} className="text-2xl">
            <strong>Payment Successful</strong>
          </p>
          <p style={{ color: "rgb(100, 116, 139)" }}>Let’s finish up your registration and get your dashboard ready.</p>
        </div>
        <div
          id="shell"
          style={{ border: "1px solid #e2e8f0", boxShadow: "0 10px 30px rgba(2,6,23,.08)", borderRadius: "16px", width: "min(720px, 100%)" }}
        >
          <div
            id="shellHeader"
            className="py-[16px] px-[18px] flex items-center"
          >
            <div id="title" className="flex items-center gap-2">
              {/* <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: `conic-gradient(from 180deg at 50% 50%, ${pollData.color}, color-mix(in hsl, ${pollData.color}, white 50%))`,
                }}
              ></div> */}
              <img src={selectedRace.raceInfo.imageUrl} alt="logo" className="w-8 h-8" />
              <span style={{ fontWeight: 700, letterSpacing: ".2px" }}>
                Hi {firstName}, welcome to {raceName}
              </span>
            </div>
          </div>
          {/* One continuous bar (no step count shown) so the questions feel short. It starts partly filled
              and only reaches the end once the answers are saved. */}
          <div
            id="progress"
            role="progressbar"
            aria-label="Registration progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
            style={{ height: "6px", background: "#e2e8f0", borderRadius: "999px", margin: "0 clamp(16px, 3vw, 24px)" }}
          >
            <div
              style={{
                height: "100%",
                width: `${progressPercent}%`,
                background: colorGradient,
                borderRadius: "999px",
                transition: "width 400ms ease",
              }}
            ></div>
          </div>
          <div id="shellContent" style={{ padding: "clamp(16px, 3vw, 24px)" }}>
            <div id="question Content">
              <div>
                <p style={{ fontSize: "clamp(18px, 3.2vw, 22px)", fontWeight: 800 }} className="mb-0">
                  {currentQuestion?.question}
                </p>
                {currentQuestion.type === "text" &&
                  currentQuestion.chipHint === true &&
                  pollData.questions[currentStep - 2]?.type === "chips" && (
                    <p className="m-0" style={{ fontSize: "12px", color: "#64748b" }}>
                      ({answers[currentStep - 2]?.answer.join(", ")})
                    </p>
                  )}
              </div>
              <div>
                {currentQuestion?.type === "chips" && <Chips></Chips>}
                {currentQuestion?.type === "text" && (
                  <div className="mb-5 mt-4 relative">
                    <textarea
                      key={answers[currentStep - 1].question}
                      name=""
                      id={`answer_step${currentStep}`}
                      className="w-full resize-y py-2 px-3 min-h-[5rem]"
                      style={{ borderColor: pollData.color, border: "1px solid #e2e8f0", borderRadius: "12px" }}
                      placeholder="Write here ..."
                      onInput={(e) => {
                        let value = e.target.value;
                        let newAnswer = [...answers];
                        newAnswer[currentStep - 1] = { question: currentQuestion?.question, answer: value };
                        setAnswers(newAnswer);
                      }}
                      defaultValue={answers[currentStep - 1].answer}
                    ></textarea>
                    {/* <p className="right-0 block w-fit absolute">
                      {Math.max(textareaMinAnswer - answers[currentStep - 1].answer.length, 0)} to go
                    </p> */}
                  </div>
                )}
              </div>
            </div>
            <div id="controls" className="flex pt-1.5 justify-between gap-[24px]">
              {currentStep > 1 && (
                <button
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: "12px 16px",
                    fontWeight: 700,
                    cursor: "pointer",
                    background: colorGradient,
                    color: "white",
                    borderRadius: "12px",
                  }}
                  onClick={() => {
                    setCurrentStep(currentStep - 1);
                  }}
                >
                  Back
                </button>
              )}
              {currentStep === 1 && <span></span>}
              <button
                style={{
                  appearance: "none",
                  border: 0,
                  padding: "12px 16px",
                  fontWeight: 700,
                  background: colorGradient,
                  color: "white",
                  borderRadius: "12px",
                  cursor: continueEnabled ? "pointer" : "not-allowed",
                  filter: continueEnabled ? "none" : "grayscale(80%)",
                }}
                onClick={nextQuestion}
              >
                {currentStep === steps && "Save and go to Dashboard"}
                {currentStep < steps && "Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </onboardingContext.Provider>
  );
};

function Chips() {
  let { currentQuestion, answers, currentStep } = useOnboardingContext();
  if (!currentQuestion?.chips) return;
  let min = currentQuestion?.min;
  let max = currentQuestion?.max;
  let selectedCount = answers[currentStep - 1]?.answer?.length;

  return (
    <>
      <p className="my-3 text-sm">
        (select {min}-{max})
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {currentQuestion.chips.map((chip) => {
          return <Chip key={chip} chip={chip}></Chip>;
        })}
      </div>
      <p className="text-sm my-0">{selectedCount} selected</p>
      {(selectedCount > currentQuestion.max || selectedCount < currentQuestion.min) && selectedCount > 0 && (
        <p className="text-sm m-0 text-red-600">
          {" "}
          Please select {min}-{max} options.
        </p>
      )}
    </>
  );
}
function Chip({ chip }) {
  let { answers, setAnswers, currentStep, colorGradient } = useOnboardingContext();
  let selected = answers[currentStep - 1]?.answer.includes(chip);
  return (
    <div
      className="chip relative inline-flex items-center justify-center py-1 px-3 min-h-[30px] rounded-full"
      style={{
        border: "1px solid #e2e8f0",
        cursor: "pointer",
        borderColor: selected ? "transparent" : "#e2e8f0",
        color: selected ? "white" : "black",
        background: selected ? colorGradient : "",
        userSelect: "none",
        fontWeight: 400,
        fontSize: "14px",
      }}
      value={chip}
      picked={selected.toString()}
      onClick={() => {
        if (selected) {
          let newAnswer = [...answers];
          newAnswer[currentStep - 1].answer.splice(newAnswer[currentStep - 1].answer.indexOf(chip), 1);
          setAnswers(newAnswer);
        } else {
          let newAnswer = [...answers];
          newAnswer[currentStep - 1].answer.push(chip);
          setAnswers(newAnswer);
        }
      }}
    >
      {chip}
    </div>
  );
}
export default Onboarding;
